import React, { useEffect, useRef, useState, useMemo } from 'react';


export interface LayoutInfo {
    scale: number;
    canvasTop: number;
    canvasLeft: number;
    outerWidthMm: number;
    outerHeightMm: number;
    contentWidthMm: number;
    contentHeightMm: number;
    frameWidthMm: number;
    matTop: number;
    matBottom: number;
    matLeft: number;
    matRight: number;
    PIXELS_PER_MM: number;
    pixelToMmScale: number;
}

interface ProductPreviewProps {
    widthMm: number;
    heightMm: number;
    imageUrl?: string;
    frameType?: string;
    borderTop?: number;
    borderBottom?: number;
    borderLeft?: number;
    borderRight?: number;
    matTop?: number;
    matBottom?: number;
    matLeft?: number;
    matRight?: number;
    productTitle?: string;
    onLayoutChange?: (layout: LayoutInfo) => void;
}

export const ProductPreview: React.FC<ProductPreviewProps> = ({
    widthMm,
    heightMm,
    imageUrl,
    frameType,
    borderTop = 0,
    borderBottom = 0,
    borderLeft = 0,
    borderRight = 0,
    matTop = 0,
    matBottom = 0,
    matLeft = 0,
    matRight = 0,
    productTitle = '',
    onLayoutChange
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [images, setImages] = useState<Record<string, HTMLImageElement>>({});
    const [loaded, setLoaded] = useState(false);
    const [artworkImage, setArtworkImage] = useState<HTMLImageElement | null>(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

    const FRAME_WIDTH_PX = 27;
    const DPI = 72;
    const PIXELS_PER_MM = DPI / 25.4;

    const hasFrame = !!frameType && frameType !== 'none';

    // Image Loading
    useEffect(() => {
        if (!imageUrl) {
            setArtworkImage(null);
            return;
        }
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => setArtworkImage(img);
        img.src = imageUrl;
    }, [imageUrl]);

    // Simulator Assets Loading
    useEffect(() => {
        const loadImages = async () => {
            const imagePaths = {
                cornerTopLeft: '/assets/simulator/aluminum_black_corner_top_left.png',
                cornerTopRight: '/assets/simulator/aluminum_black_corner_top_right.png',
                edgeTop: '/assets/simulator/aluminum_black_edge_top.png',
                edgeBottom: '/assets/simulator/aluminum_black_edge_bottom.png',
                edgeLeft: '/assets/simulator/aluminum_black_edge_left.png',
                edgeRight: '/assets/simulator/aluminum_black_edge_right.png',
            };

            const loadedImages: Record<string, HTMLImageElement> = {};
            const promises = Object.entries(imagePaths).map(([key, src]) => {
                return new Promise<void>((resolve) => {
                    const img = new Image();
                    img.onload = () => {
                        loadedImages[key] = img;
                        resolve();
                    };
                    img.onerror = () => {
                        console.warn(`Failed to load image: ${src}`);
                        resolve();
                    };
                    img.src = src;
                });
            });

            await Promise.all(promises);
            setImages(loadedImages);
            setLoaded(true);
        };
        loadImages();
    }, []);

    // Resize Handler
    useEffect(() => {
        const updateSize = () => {
            if (containerRef.current) {
                setContainerSize({
                    width: containerRef.current.clientWidth,
                    height: containerRef.current.clientHeight
                });
            }
        };
        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    // Determine Mode
    const isException = useMemo(() => {
        const keywords = ['ブックマット', 'オーバーマット'];
        return keywords.some(k => productTitle.includes(k));
    }, [productTitle]);

    // Layout Calculation
    const layout = useMemo(() => {
        if (containerSize.width === 0 || containerSize.height === 0) return null;

        const frameWidthMm = hasFrame ? (FRAME_WIDTH_PX / PIXELS_PER_MM) : 0;

        // Content Size (Paper or Mat Outer)
        const contentWidthMm = widthMm;
        const contentHeightMm = heightMm;

        // Total Size (Content + Frame)
        // Frame is drawn OUTSIDE the content.
        const outerWidthMm = contentWidthMm + (frameWidthMm * 2);
        const outerHeightMm = contentHeightMm + (frameWidthMm * 2);

        const padding = 100; // Increased padding to avoid overlap with bottom buttons
        const availWidth = containerSize.width - padding * 2;
        const availHeight = containerSize.height - padding * 2;

        const outerWidthPxRaw = outerWidthMm * PIXELS_PER_MM;
        const outerHeightPxRaw = outerHeightMm * PIXELS_PER_MM;

        const scaleX = availWidth / outerWidthPxRaw;
        const scaleY = availHeight / outerHeightPxRaw;
        const scale = Math.min(scaleX, scaleY);

        const canvasTop = (containerSize.height - (outerHeightPxRaw * scale)) / 2;
        const canvasLeft = (containerSize.width - (outerWidthPxRaw * scale)) / 2;

        return {
            scale,
            canvasTop,
            canvasLeft,
            outerWidthMm,
            outerHeightMm,
            contentWidthMm,
            contentHeightMm,
            frameWidthMm,
            PIXELS_PER_MM
        };
    }, [widthMm, heightMm, containerSize, hasFrame, PIXELS_PER_MM]);

    // Notify Parent of Layout Changes
    useEffect(() => {
        if (layout && onLayoutChange) {
            onLayoutChange({
                ...layout,
                matTop,
                matBottom,
                matLeft,
                matRight,
                pixelToMmScale: layout.scale * layout.PIXELS_PER_MM,
                PIXELS_PER_MM: layout.PIXELS_PER_MM
            });
        }
    }, [layout, onLayoutChange, matTop, matBottom, matLeft, matRight]);

    // Drawing
    useEffect(() => {
        if (!loaded || !canvasRef.current || !layout) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const { scale, outerWidthMm, outerHeightMm, contentWidthMm, contentHeightMm, frameWidthMm, canvasLeft, canvasTop } = layout;

        const dpr = window.devicePixelRatio || 1;

        // Use container size for canvas to allow drawing dimensions outside the product area
        canvas.width = containerSize.width * dpr;
        canvas.height = containerSize.height * dpr;
        canvas.style.width = `${containerSize.width}px`;
        canvas.style.height = `${containerSize.height}px`;
        canvas.style.position = 'absolute';
        canvas.style.top = '0';
        canvas.style.left = '0';

        ctx.scale(dpr, dpr);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // Clear entire canvas
        ctx.clearRect(0, 0, containerSize.width, containerSize.height);

        // Translate to centered position
        ctx.save();
        ctx.translate(canvasLeft, canvasTop);

        const finalWidth = outerWidthMm * PIXELS_PER_MM * scale;
        const finalHeight = outerHeightMm * PIXELS_PER_MM * scale;

        // Calculate Rects (in Canvas Coords relative to (0,0) of the translated origin)
        // (0,0) is top-left of Outer Rect.
        // Content Rect is offset by FrameWidth.
        const frameOffset = frameWidthMm * PIXELS_PER_MM * scale;
        const contentX = frameOffset;
        const contentY = frameOffset;
        const contentW = contentWidthMm * PIXELS_PER_MM * scale;
        const contentH = contentHeightMm * PIXELS_PER_MM * scale;

        // Helper to draw frame
        const drawFrameAssets = (withShadow: boolean) => {
            if (!hasFrame) return;

            const scaledCornerSize = Math.max(images.cornerTopLeft?.width || 0, images.cornerTopLeft?.height || 0) * scale * 0.5;
            const scaledFrameWidth = frameOffset;

            ctx.save();

            if (withShadow) {
                ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
                ctx.shadowBlur = 30;
                ctx.shadowOffsetX = 15;
                ctx.shadowOffsetY = 15;
            }

            const drawEdge = (side: 'top' | 'bottom' | 'left' | 'right', x: number, y: number, w: number, h: number) => {
                let img;
                switch (side) {
                    case 'top': img = images.edgeTop; break;
                    case 'bottom': img = images.edgeBottom; break;
                    case 'left': img = images.edgeLeft; break;
                    case 'right': img = images.edgeRight; break;
                }
                if (!img) return;

                if (side === 'top' || side === 'bottom') {
                    ctx.drawImage(img, 0, 0, img.width, img.height, x, y, w, scaledFrameWidth);
                } else {
                    ctx.drawImage(img, 0, 0, img.width, img.height, x, y, scaledFrameWidth, h);
                }
            };

            const drawCorner = (pos: 'tl' | 'tr' | 'bl' | 'br', x: number, y: number) => {
                let img;
                let rotation = 0;
                switch (pos) {
                    case 'tl': img = images.cornerTopLeft; break;
                    case 'tr': img = images.cornerTopRight; break;
                    case 'bl': img = images.cornerTopRight; rotation = 180; break;
                    case 'br': img = images.cornerTopLeft; rotation = 180; break;
                }
                if (!img) return;

                const size = scaledCornerSize;
                ctx.save();
                const cx = x + size / 2;
                const cy = y + size / 2;
                ctx.translate(cx, cy);
                ctx.rotate(rotation * Math.PI / 180);
                ctx.translate(-cx, -cy);
                ctx.drawImage(img, x, y, size, size);
                ctx.restore();
            };

            // Edges
            const horizEdgeW = finalWidth - scaledCornerSize * 2;
            if (horizEdgeW > 0) {
                drawEdge('top', scaledCornerSize, 0, horizEdgeW, scaledFrameWidth);
                drawEdge('bottom', scaledCornerSize, finalHeight - scaledFrameWidth, horizEdgeW, scaledFrameWidth);
            }
            // Left/Right
            const vertEdgeH = finalHeight - scaledCornerSize * 2;
            if (vertEdgeH > 0) {
                drawEdge('left', 0, scaledCornerSize, scaledFrameWidth, vertEdgeH);
                drawEdge('right', finalWidth - scaledFrameWidth, scaledCornerSize, scaledFrameWidth, vertEdgeH);
            }

            // Corners
            drawCorner('tl', 0, 0);
            drawCorner('tr', finalWidth - scaledCornerSize, 0);
            drawCorner('br', finalWidth - scaledCornerSize, finalHeight - scaledCornerSize);
            drawCorner('bl', 0, finalHeight - scaledCornerSize);

            ctx.restore();
        };

        // 1. Draw Frame Shadow (Behind content)
        if (hasFrame) {
            drawFrameAssets(true);
        }

        // 2. Draw Content Background (White) - Covers inner shadow
        ctx.save();
        ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
        ctx.shadowBlur = 30;
        ctx.shadowOffsetX = 10;
        ctx.shadowOffsetY = 10;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(contentX, contentY, contentW, contentH);
        ctx.restore();

        // Determine "Print/Visible Region"
        // Includes Mat Width Offset + Border
        let regionX = contentX + ((matLeft + borderLeft) * PIXELS_PER_MM * scale);
        let regionY = contentY + ((matTop + borderTop) * PIXELS_PER_MM * scale);
        let regionW = contentW - ((matLeft + borderLeft + matRight + borderRight) * PIXELS_PER_MM * scale);
        let regionH = contentH - ((matTop + borderTop + matBottom + borderBottom) * PIXELS_PER_MM * scale);

        // 3. Draw Artwork (Clipped to Region)
        if (artworkImage) {
            const imgAspect = artworkImage.width / artworkImage.height;
            const regionAspect = regionW / regionH;

            let drawW, drawH, drawX, drawY;

            if (imgAspect > regionAspect) {
                drawW = regionW;
                drawH = regionW / imgAspect;
                drawX = regionX;
                drawY = regionY + (regionH - drawH) / 2;
            } else {
                drawH = regionH;
                drawW = regionH * imgAspect;
                drawY = regionY;
                drawX = regionX + (regionW - drawW) / 2;
            }

            ctx.save();
            ctx.beginPath();
            ctx.rect(regionX, regionY, regionW, regionH);
            ctx.clip();
            ctx.drawImage(artworkImage, drawX, drawY, drawW, drawH);
            ctx.restore();
        }

        // 3.5 Draw Mat Board with Strong Inner Shadow (If Mat Product)
        if (isException) {
            ctx.save();
            ctx.beginPath();
            // Outer Rect (Full Content Size)
            ctx.rect(contentX, contentY, contentW, contentH);
            // Inner Rect (Window Size)
            // Hole = content + mat.
            const holeX = contentX + (matLeft * PIXELS_PER_MM * scale);
            const holeY = contentY + (matTop * PIXELS_PER_MM * scale);
            const holeW = contentW - ((matLeft + matRight) * PIXELS_PER_MM * scale);
            const holeH = contentH - ((matTop + matBottom) * PIXELS_PER_MM * scale);

            ctx.rect(holeX, holeY, holeW, holeH);

            ctx.fillStyle = '#ffffff';
            ctx.shadowColor = 'rgba(0, 0, 0, 0.7)'; // Strong shadow
            ctx.shadowBlur = 15;
            ctx.shadowOffsetX = 4;
            ctx.shadowOffsetY = 4;

            ctx.fill('evenodd');
            ctx.restore();
        }

        // 4. Draw Frame Body (On top, no shadow)
        if (hasFrame) {
            drawFrameAssets(false);
        }

        // --- Helper functions for Dimensions ---

        const drawLine = (x1: number, y1: number, x2: number, y2: number, color: string, dashed = false) => {
            ctx.save();
            ctx.beginPath();
            ctx.strokeStyle = color;
            ctx.lineWidth = 2;
            if (dashed) ctx.setLineDash([5, 5]);
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();
            ctx.restore();
        };

        const drawRect = (x: number, y: number, w: number, h: number, color: string, dashed = false) => {
            ctx.save();
            ctx.beginPath();
            ctx.strokeStyle = color;
            ctx.lineWidth = 2;
            if (dashed) ctx.setLineDash([5, 5]);
            ctx.rect(x, y, w, h);
            ctx.stroke();
            ctx.restore();
        };

        const drawMarker = (label: string, x1: number, y1: number, x2: number, y2: number, color: string = 'red') => {
            // Draw double arrow and text
            ctx.save();
            ctx.strokeStyle = color;
            ctx.fillStyle = color;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();

            // Arrowheads
            const headLen = 6;
            const angle = Math.atan2(y2 - y1, x2 - x1);

            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x1 + headLen * Math.cos(angle + Math.PI / 6), y1 + headLen * Math.sin(angle + Math.PI / 6));
            ctx.moveTo(x1, y1);
            ctx.lineTo(x1 + headLen * Math.cos(angle - Math.PI / 6), y1 + headLen * Math.sin(angle - Math.PI / 6));
            ctx.stroke();

            ctx.beginPath();
            ctx.moveTo(x2, y2);
            ctx.lineTo(x2 - headLen * Math.cos(angle + Math.PI / 6), y2 - headLen * Math.sin(angle + Math.PI / 6));
            ctx.moveTo(x2, y2);
            ctx.lineTo(x2 - headLen * Math.cos(angle - Math.PI / 6), y2 - headLen * Math.sin(angle - Math.PI / 6));
            ctx.stroke();

            // Text
            ctx.font = 'bold 12px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';

            const midX = (x1 + x2) / 2;
            const midY = (y1 + y2) / 2;

            const textY = midY + 5;

            // Text Background
            const textMetrics = ctx.measureText(label);
            const pad = 2;
            ctx.fillStyle = 'white';
            const textHeight = 12;
            ctx.fillRect(midX - textMetrics.width / 2 - pad, textY - pad, textMetrics.width + pad * 2, textHeight + pad * 2);

            ctx.fillStyle = color;
            ctx.fillText(label, midX, textY);

            ctx.restore();
        };

        // --- Guidelines & Dimensions ---

        const blackStyle = '#000000';
        const blueStyle = '#0000FF';
        const greenStyle = '#008000';
        const redStyle = '#FF0000';

        // 1. Black Solid Rect (Delivery Size / Mat Outer)
        // drawRect(contentX, contentY, contentW, contentH, blackStyle, false);

        // 2. Exception Mode Extra: Blue Dashed Rect (Paper Size)
        // If Exception, we draw this.
        if (isException) {
            const paperMarginMm = 2.5;
            // Region X here includes border, so paperX based on regionX moves with border.
            // But Paper Size should be relative to Mat Window (Hole).
            // Hole = content + mat.
            const holeX = contentX + (matLeft * PIXELS_PER_MM * scale);
            const holeY = contentY + (matTop * PIXELS_PER_MM * scale);
            const holeW = contentW - ((matLeft + matRight) * PIXELS_PER_MM * scale);
            const holeH = contentH - ((matTop + matBottom) * PIXELS_PER_MM * scale);

            const paperX = holeX - (paperMarginMm * PIXELS_PER_MM * scale);
            const paperY = holeY - (paperMarginMm * PIXELS_PER_MM * scale);
            const paperW = holeW + (5 * PIXELS_PER_MM * scale);
            const paperH = holeH + (5 * PIXELS_PER_MM * scale);

            // drawRect(paperX, paperY, paperW, paperH, blueStyle, true); // Hidden
        }

        // 3. Green Rect (Print Region / Image)
        // drawRect(regionX, regionY, regionW, regionH, greenStyle, isException);

        // 4. Dimensions

        const dimOffset = 50;
        // Delivery Width (Top)
        drawMarker(`${contentWidthMm}mm`, contentX, contentY - dimOffset, contentX + contentW, contentY - dimOffset, blackStyle);
        // Delivery Height (Left)
        drawMarker(`${contentHeightMm}mm`, contentX - dimOffset, contentY, contentX - dimOffset, contentY + contentH, blackStyle);

        // 5. Margins (Red Arrows)
        // For Border calculations, we show distance from Mat Limit (Hole/Paper start) to Print Region.
        // Start Point: content + mat
        // End Point: content + mat + border  (which is region)

        const startX = contentX + (matLeft * PIXELS_PER_MM * scale);
        const startY = contentY + (matTop * PIXELS_PER_MM * scale);
        const startW = contentW - ((matLeft + matRight) * PIXELS_PER_MM * scale);
        const startH = contentH - ((matTop + matBottom) * PIXELS_PER_MM * scale);

        // Top
        if (borderTop > 0) {
            drawMarker(`${borderTop}mm`, contentX + contentW / 2, startY, contentX + contentW / 2, regionY, redStyle);
        }
        // Bottom
        if (borderBottom > 0) {
            drawMarker(`${borderBottom}mm`, contentX + contentW / 2, regionY + regionH, contentX + contentW / 2, startY + startH, redStyle);
        }
        // Left
        if (borderLeft > 0) {
            drawMarker(`${borderLeft}mm`, startX, regionY + regionH / 2, regionX, regionY + regionH / 2, redStyle);
        }
        // Right
        if (borderRight > 0) {
            drawMarker(`${borderRight}mm`, regionX + regionW, regionY + regionH / 2, startX + startW, regionY + regionH / 2, redStyle);
        }

        ctx.restore();

    }, [loaded, layout, artworkImage, hasFrame, isException, images, borderTop, borderBottom, borderLeft, borderRight, matTop, matBottom, matLeft, matRight, PIXELS_PER_MM]);

    return (
        <div
            ref={containerRef}
            className="relative w-full h-full overflow-hidden flex items-center justify-center bg-gray-100"
        >

            <canvas ref={canvasRef} />

        </div>
    );
};
